import { Economy, LevelCatalog } from "@highmarch/app";
import { GearItem, MarketOffer, Rarity, salvageYield, secondsToNextDay, sellBlocker, sellPrice } from "@highmarch/domain";

/**
 * The Market page as data (FINISH_PLAN WP5; owner decision D7; M13, M14), no DOM (marketModel.test.ts): the gear
 * the Trading Post would buy from the player and for how much (always more than salvage), and the Black Market's one
 * featured item a day, priced in gems. The Trading Post's buy list moved to the Mystery Merchant (Shops, shop pass 2).
 */

export interface OfferCard {
  readonly offer: MarketOffer;
  readonly bought: boolean;
  readonly affordable: boolean;
  readonly short: number;
}

export interface SellRow {
  readonly item: GearItem;
  readonly marks: number;
  /** What salvage would pay for it instead, in gold (the page says a sale beats it). */
  readonly salvageTotal: number;
  /** A Mythic's effect is lost for good: the sale asks once more. */
  readonly mythic: boolean;
}

export interface MarketModel {
  readonly marks: number;
  readonly gems: number;
  /** Spare gear the Trading Post buys, most valuable first. */
  readonly sellable: readonly SellRow[];
  /** Rare+ gear it cannot take now (worn, locked or bound): counted so the page can say why it is missing. */
  readonly heldBack: number;
  readonly blackMarket: OfferCard | null;
  readonly resetIn: number;
  /** false in the quiet after a loss: the Black Market card drops its "featured" dressing (ENGAGEMENT C). */
  readonly nudges: boolean;
}

export function sellRows(catalog: LevelCatalog): { rows: SellRow[]; heldBack: number } {
  const B = catalog.balance, minRarity = B.commerce.market.minSellRarity;
  const rows: SellRow[] = [];
  let heldBack = 0;
  for (const item of catalog.profile.gear) {
    if (item.rarity < minRarity) continue;
    if (sellBlocker(B, item) !== null) { heldBack++; continue; }
    const s = salvageYield(B, item).gold;
    rows.push({ item, marks: sellPrice(B, item), salvageTotal: s, mythic: item.rarity === Rarity.Mythic });
  }
  rows.sort((a, b) => b.marks - a.marks || b.item.rarity - a.item.rarity || a.item.id - b.item.id);
  return { rows, heldBack };
}

export function marketModel(catalog: LevelCatalog, now: number): MarketModel {
  const eco = new Economy(catalog), w = eco.wallet;
  const card = (offer: MarketOffer, bought: boolean): OfferCard => {
    const have = offer.currency === "gems" ? w.gems : w.marks;
    return { offer, bought, affordable: have >= offer.price, short: Math.max(0, offer.price - have) };
  };
  const bm = eco.blackMarket(now);
  const { rows, heldBack } = sellRows(catalog);
  return {
    marks: w.marks, gems: w.gems, sellable: rows, heldBack,
    blackMarket: bm ? card(bm.offer, bm.bought) : null, resetIn: secondsToNextDay(now), nudges: eco.offersAllowed("hub", now),
  };
}
